import { CircularProgress, Grid, MenuItem, Paper, Switch, TextField } from '@material-ui/core';
import { debounce } from 'lodash-es';
import moment from 'moment-timezone';
import React from 'react';

import {
  createReport,
  IReport,
  ReportActive,
  ReportInactive,
  REPORTTYPE_HEADERBIDDER_SSP,
  sendEmail,
  updateReport,
} from '~/src/dash/api/console-api';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import { isIASReport } from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { CategoryMultiSelect } from '~/src/dash/components/dropdowns/category-multiselect';
import { ISelectModel } from '~/src/dash/components/model';
import { ModelPrimaryButton } from '~/src/dash/components/model-primary-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { TIMEZONES } from '~/src/dash/globals/timezone';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  createDownloadReportURL,
  extractRouteParams,
  genReportURL,
  navigate,
  RouteSSPReportList,
} from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { getAllCountries } from '~/src/dash/utils/countries';
import { formatDate, getEndDate, getStartDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { deviceTypes } from '~/src/dash/utils/devices';
import { generateEmailParams, generateReportParams, toSQLOption } from '~/src/dash/utils/graph';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import {
  errorColor,
  growGrid,
  growGridLeft,
  growGridRight,
} from '~/src/dash/utils/material-dashboard-react';
import {
  frequency,
  FrequencyMonthly,
  FrequencyWeekly,
  getColumnIndex,
  getGroupColumnsByReportID,
  getMetricColumnsByReportID,
  IColumn,
  period,
  scheduledDayMonthly,
  scheduledDayWeekly,
} from '~/src/dash/utils/report-fields';
import { routeName } from '~/src/dash/utils/routes';
import { hasErrors, IErrors, validatorFactory } from '~/src/dash/utils/validator';
import { ButtonView } from '~/src/dash/views/button-view';
import { H1, H2, RowButton } from '~/src/dash/views/component-view';
import { FormRecipientView } from '~/src/dash/views/form-email-view';
import { SingleSelect } from '~/src/dash/views/singleselect-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  report: IReport;
}

interface IState extends IBaseState, IReport {
  dateStart: moment.Moment;
  dateEnd: moment.Moment;
  data: any;

  updating: boolean;
  countUpdating: number;
  errors: IErrors;
  sending: boolean;
  countSending: number;

  snackbarMessage: string;
  snackbarError: boolean;
}

function isExistingReport(report: IReport) {
  return !!report.ID;
}

const validate = validatorFactory({
  ReportType(val: any): string {
    if (!val) {
      return 'Select a report type';
    }
    return '';
  },
  BidderCodes(val: any): string {
    if (!val || !val.length) {
      return 'Select a bidder';
    }
    return '';
  },
  Metrics(val: any): string {
    if (!val || !val.length) {
      return 'Select a report metric';
    }
    return '';
  },
  GroupByOptions(val: any): string {
    if (!val || !val.length) {
      return 'Select a report groupby option';
    }
    return '';
  },
  Name(val: any): string {
    if (!val) {
      return 'Enter a custom report name';
    }
    return '';
  },
  Subject(val: any): string {
    if (!val) {
      return 'Enter a custom email subject line';
    }
    return '';
  },
  Frequency(val: any): string {
    if (!val) {
      return "Select the report data's frequency";
    }
    return '';
  },
  Period(val: any): string {
    if (!val) {
      return "Select a report data's period";
    }
    return '';
  },
});

export const SSPReportFormView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      siteSelectButton: {},
      reportNameContainer: {
        display: 'inline-block',
        width: 'calc(50% - 2em)',
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
      datePickerSection: {
        display: 'inline-flex',
        'justify-content': 'center',
        'align-items': 'center',
      },
      growGrid,
      growGridLeft,
      growGridRight,
      errorColor: {
        color: errorColor,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = (() => {
      return Object.assign(this.props.report, {
        ReportType: REPORTTYPE_HEADERBIDDER_SSP,
        dateStart: getStartDate(this.props.report.Timezone || 'UTC', 6, 'day'),
        dateEnd: getEndDate(this.props.report.Timezone || 'UTC'),
        updating: false,
        countUpdating: 0,
        errors: {} as IErrors,
        sending: false,
        countSending: 0,
        data: [],
        snackbarMessage: '',
        snackbarError: false,
      });
    })();

    _saveReport = debounce((countUpdating: number) => {
      if (countUpdating !== this.state.countUpdating) {
        return;
      }
      if (!this.validateForm()) {
        this.setState({
          snackbarMessage:
            'Saving report failed. Please fill out the required fields and try again.',
          snackbarError: true,
          updating: false,
        });
        return;
      }
      (isExistingReport(this.state) ? updateReport : createReport)({
        query: Object.assign(
          {
            publisher_id: this.props.session.publisher.ID,
            report: this.state,
          },
          isExistingReport(this.state)
            ? {
                report_id: this.state.ID,
              }
            : {},
        ),
      }).then(
        (report: IReport) => {
          this.setState(
            Object.assign(this.state, report, {
              snackbarMessage: 'Successfully saved report',
              snackbarError: false,
              updating: false,
            }),
          );
        },
        () => {
          this.setState({
            updating: false,
          });
        },
      );
    }, 500);

    _sendEmail = debounce((countSending: number) => {
      if (countSending !== this.state.countSending) {
        return;
      }
      if (!this.validateForm()) {
        this.setState({
          snackbarMessage:
            'Sending report email failed. Please fix the report form and try again later.',
          snackbarError: true,
          sending: false,
        });
        return;
      }
      sendEmail({
        query: generateEmailParams(this.state, {
          publisher_id: this.props.session.publisher.ID,
          report_name: this.state.Name,
          start_date: formatDate(this.state.dateStart),
          end_date: formatDate(this.state.dateEnd),
          timezone: this.state.Timezone,
          currencydisplayed: ECurrencyType.CURRENCY_TYPE_USD,
        }),
      }).then(
        () => {
          if (countSending !== this.state.countSending) {
            return;
          }
          this.setState({
            snackbarMessage: 'Please allow 5 minutes for your report to appear in your inbox.',
            snackbarError: false,
            sending: false,
          });
        },
        () => {
          if (countSending !== this.state.countSending) {
            return;
          }
          this.setState({
            sending: false,
          });
        },
      );
    }, 200);

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          [target.name]: target.value,
        },
        () => {
          if (hasErrors(this.state.errors)) {
            this.validateForm();
          }
        },
      );
    };

    handleChangeSelect = (name: string, value: string[]) => {
      this.setState(
        {
          [name]: name === 'BidderCodes' ? [value] : value,
        },
        () => {
          if (hasErrors(this.state.errors)) {
            this.validateForm();
          }
        },
      );
    };

    handleChangeSelectWithSorting = (name: string, value: string[], selection: IColumn[]) => {
      this.handleChangeSelect(
        name,
        value.sort((k1: string, k2: string) => {
          return getColumnIndex(k1, selection) - getColumnIndex(k2, selection);
        }),
      );
    };

    handleDateChange = ({ start, end }: IDateRangeChangeEvent) => {
      this.setState({
        dateStart: start,
        dateEnd: end,
      });
    };

    handleChangeRecipients = (Recipients: string[]) => {
      this.setState({
        Recipients,
      });
    };

    handleChangeActive = () => {
      this.setState({
        Active: this.state.Active === ReportActive ? ReportInactive : ReportActive,
      });
    };

    generateReportParams = (includeQuotes: boolean) => {
      return generateReportParams(this.state, {
        publisher_id: this.props.session.publisher.ID,
        start_date: formatDate(this.state.dateStart),
        end_date: formatDate(this.state.dateEnd),
        currency: ECurrencyType.CURRENCY_TYPE_USD,
        exchangerate: 1, // default to using usd for ssp report
        timezone: this.state.Timezone,
        includeQuotes,
        report_type: this.state.ReportType,
      });
    };

    openReport = () => {
      if (!this.validateForm()) {
        this.setState({
          snackbarMessage:
            'Request to preview report failed. Please fill out the required fields and try again.',
          snackbarError: true,
        });
        return;
      }
      window.open(genReportURL(this.generateReportParams(true)), '_blank');
    };

    handleDownload = () => {
      this.setState({
        snackbarMessage: 'Report is generating and will automatically download when ready.',
        snackbarError: false,
      });
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    gotoReportListView = () => {
      navigate(
        RouteSSPReportList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    validateForm = (): boolean => {
      const errors = validate(this.state);
      if (hasErrors(errors)) {
        this.setState({
          errors,
        });
        return false;
      }
      this.setState({
        errors: {} as IErrors,
      });
      return true;
    };

    saveReport = (fromScheduled: boolean = false) => {
      if (fromScheduled && this.state.Recipients.length <= 0) {
        this.setState({
          snackbarMessage: "Can't save report as recipient(s) are empty.",
          snackbarError: true,
        });
        return;
      }

      const countUpdating = this.state.countUpdating + 1;
      this.setState({
        updating: true,
        countUpdating,
      });
      this._saveReport(countUpdating);
    };

    sendEmail = () => {
      if (this.state.Recipients.length <= 0) {
        this.setState({
          snackbarMessage: "Can't send report as recipient(s) are empty.",
          snackbarError: true,
        });
        return;
      }
      const countSending = this.state.countSending + 1;
      this.setState({
        sending: true,
        countSending,
      });
      this._sendEmail(countSending);
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;

      const SSPBiddersList: Array<{ label: string; value: string }> =
        getHeaderBidderAdaptersForPublisher('', true).map((bidder) => {
          return {
            value: bidder.key,
            label: bidder.text,
          };
        }); // this, this.state.ssp? SingleSelect Component requires these keys

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {isExistingReport(this.state) ? (
              <H1>
                <span>SSP Reports: </span>
                <span className={classes.reportNameContainer}>
                  <TextField
                    name='Name'
                    fullWidth
                    value={isExistingReport(this.state) ? this.state.Name : 'New'}
                    onChange={this.handleChange}
                    error={!!this.state.errors.Name}
                    helperText={this.state.errors.Name}
                  />
                </span>
              </H1>
            ) : (
              <H1>SSP Reports: New</H1>
            )}
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <H2>Options</H2>
              <div className={classes.section}>
                <Grid container>
                  <Grid item xs={6}>
                    <DateTimeRangeComponent
                      timezone={this.state.Timezone}
                      start={this.state.dateStart}
                      end={this.state.dateEnd}
                      onChange={this.handleDateChange}
                      disableTime={true}
                      enableSelectAll={true}
                    />
                  </Grid>
                  <Grid item xs={6} justify='flex-end'>
                    <ModelPrimaryButton
                      variant='contained'
                      color='primary'
                      onClick={() => this.saveReport()}
                      disabled={this.state.updating}
                    >
                      {this.state.updating && (
                        <CircularProgress size={12} style={{ color: 'white' }} />
                      )}
                      Save
                    </ModelPrimaryButton>
                    <ModelSecondaryButton onClick={this.gotoReportListView}>
                      Close
                    </ModelSecondaryButton>
                    <ModelSecondaryButton onClick={this.openReport}>Preview</ModelSecondaryButton>
                    {true && (
                      // @ts-ignore
                      <ModelSecondaryButton
                        // @ts-ignore
                        download
                        href={
                          !!this.state.BidderCodes.length &&
                          !!this.state.Metrics.length &&
                          !!this.state.GroupByOptions.length
                            ? createDownloadReportURL(
                                this.generateReportParams(false),
                                isIASReport(this.state.ReportType),
                              )
                            : '#'
                        }
                        disabled={
                          !this.state.BidderCodes.length ||
                          !this.state.Metrics.length ||
                          !this.state.GroupByOptions.length
                        }
                      >
                        Download
                      </ModelSecondaryButton>
                    )}
                  </Grid>
                </Grid>
              </div>
              <div
                className={classes.section}
                style={{
                  width: '400px',
                }}
              >
                <SingleSelect
                  name='Timezone'
                  label='Timezone'
                  onChange={this.handleChangeSelect}
                  value={this.state.Timezone}
                  items={TIMEZONES}
                />
              </div>
              <div className={classes.section}>
                <SingleSelect
                  label='Select Demand Source'
                  name='BidderCodes'
                  items={SSPBiddersList}
                  onChange={this.handleChangeSelect}
                  value={this.state.BidderCodes[0]}
                  helperText={this.state.errors.BidderCodes}
                  error={!!this.state.errors.BidderCodes}
                  disableAlphanumSort={true}
                />
              </div>
              <div className={classes.section}>
                <CategoryMultiSelect
                  label='Filter by Country'
                  filterHelperText='Search for country'
                  name='Countries'
                  items={getAllCountries()}
                  onChange={this.handleChangeSelect}
                  values={this.state.Countries}
                  helperText={this.state.errors.Countries}
                  search
                />
              </div>
              <div className={classes.section}>
                <CategoryMultiSelect
                  label='Filter by Device Type'
                  name='DeviceTypes'
                  items={deviceTypes}
                  onChange={this.handleChangeSelect}
                  values={this.state.DeviceTypes}
                  helperText={this.state.errors.DeviceTypes}
                />
              </div>
              <div className={classes.section}>
                <CategoryMultiSelect
                  label='Metrics'
                  name='Metrics'
                  subCategories={[
                    { title: 'Ratios', matchRegex: '.*Rate' },
                    {
                      title: 'Bids',
                      matchRegex:
                        /(Bid Requests|Bid Responses|^Winning Bids|Impressions|Lost Bids)/,
                    },
                    { title: 'Revenue', matchRegex: /(^Average .*|^Revenue$)/ },
                    {
                      title: 'Errors',
                      matchRegex: /(Bid Errors|Bid Timeouts|Rejected Winning Bids)/,
                    },
                    { title: 'Others', matchRegex: /./ },
                  ]}
                  items={getMetricColumnsByReportID(
                    this.state.ReportType,
                    this.props.session.publisher.ID,
                    [],
                    this.state.GroupByOptions,
                    this.props.user.user,
                  ).map(toSQLOption)}
                  onChange={(name: string, value: string[]) => {
                    this.handleChangeSelectWithSorting(
                      name,
                      value,
                      getMetricColumnsByReportID(
                        this.state.ReportType,
                        this.props.session.publisher.ID,
                        [],
                        this.state.GroupByOptions || [],
                        this.props.user.user,
                      ),
                    );
                  }}
                  values={this.state.Metrics}
                  helperText={this.state.errors.Metrics}
                  error={!!this.state.errors.Metrics}
                />
              </div>
              <div className={classes.section}>
                <CategoryMultiSelect
                  label='Group By'
                  name='GroupByOptions'
                  items={getGroupColumnsByReportID(
                    this.state.ReportType,
                    this.props.session.publisher.ID,
                  ).map(toSQLOption)}
                  onChange={(name: string, value: string[]) => {
                    this.handleChangeSelectWithSorting(
                      name,
                      value,
                      getGroupColumnsByReportID(
                        this.state.ReportType,
                        this.props.session.publisher.ID,
                      ),
                    );
                  }}
                  values={this.state.GroupByOptions}
                  helperText={this.state.errors.GroupByOptions}
                  error={!!this.state.errors.GroupByOptions}
                />
              </div>
            </Paper>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <RowButton
                left={<H2>Schedule</H2>}
                right={
                  <Switch
                    checked={this.state.Active === ReportActive}
                    onChange={this.handleChangeActive}
                  />
                }
              />
              {this.state.Active === ReportActive && (
                <div>
                  {!isExistingReport(this.state) && (
                    <div className={classes.section}>
                      <TextField
                        label='Report Name'
                        name='Name'
                        fullWidth
                        value={this.state.Name}
                        onChange={this.handleChange}
                        error={!!this.state.errors.Name}
                        helperText={this.state.errors.Name}
                      />
                    </div>
                  )}
                  <div className={classes.section}>
                    <TextField
                      label='Subject Name'
                      name='Subject'
                      fullWidth
                      value={this.state.Subject}
                      onChange={this.handleChange}
                      error={!!this.state.errors.Subject}
                      helperText={this.state.errors.Subject}
                    />
                  </div>
                  <div className={classes.section}>
                    <TextField
                      select
                      fullWidth
                      label='Frequency'
                      name='Frequency'
                      value={this.state.Frequency}
                      onChange={this.handleChange}
                      margin='normal'
                      error={!!this.state.errors.Frequency}
                      helperText={this.state.errors.Frequency}
                    >
                      {frequency.map(({ key, text }: ISelectModel) => {
                        return (
                          <MenuItem key={key} value={key}>
                            {text}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </div>
                  {(this.state.Frequency === FrequencyWeekly ||
                    this.state.Frequency === FrequencyMonthly) && (
                    <div className={classes.section}>
                      <TextField
                        select
                        fullWidth
                        label='Scheduled Day'
                        name='ScheduledDay'
                        value={this.state.ScheduledDay}
                        onChange={this.handleChange}
                        margin='normal'
                        error={!!this.state.errors.ScheduledDay}
                        helperText={this.state.errors.ScheduledDay}
                      >
                        {(this.state.Frequency === FrequencyWeekly
                          ? scheduledDayWeekly
                          : scheduledDayMonthly
                        ).map(({ key, text }: ISelectModel) => {
                          return (
                            <MenuItem key={key} value={key}>
                              {text}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </div>
                  )}
                  <div className={classes.section}>
                    <TextField
                      select
                      fullWidth
                      label='Period'
                      name='Period'
                      value={this.state.Period}
                      onChange={this.handleChange}
                      margin='normal'
                      error={!!this.state.errors.Period}
                      helperText={this.state.errors.Period}
                    >
                      {period.map(({ key, text }: ISelectModel) => {
                        return (
                          <MenuItem key={key} value={key}>
                            {text}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </div>
                  <div className={classes.section}>
                    <FormRecipientView
                      onChange={this.handleChangeRecipients}
                      value={this.state.Recipients}
                    />
                  </div>
                  <div className={classes.section}>
                    <ButtonView
                      style={{ marginRight: '8px' }}
                      onClick={() => this.saveReport(true)}
                      text={
                        isExistingReport(this.state) ? 'Save Scheduled Report' : 'Create Schedule'
                      }
                      active={this.state.updating}
                    />
                    <ButtonView
                      onClick={this.sendEmail}
                      text='Send Now'
                      active={this.state.sending}
                    />
                  </div>
                </div>
              )}
            </Paper>
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
